import { useFormik } from "formik";
import { useEffect, useState } from "react";
import MESSAGES from "../../../../common/data/constants/messages";
import { IService } from "../../../../common/data/dtos/service";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import serviceService from "../../../../services/service-service";

type ServiceFormValues = {
    id?: string;
    description: string;
    unit: string;
};

interface Props {
    isModalOpened: boolean;
    selectedService: IService | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const ServiceModal = ({ isModalOpened, selectedService, onModalClose, onAfterSubmit }: Props) => {
    const formik = useFormik({
        initialValues: {
            description: selectedService?.description || '',
            unit: selectedService?.unit || '',
        },
        validate: (values) => {
            const errors: {
                description?: string;
            } = {};

            if (!values.description) {
                errors.description = MESSAGES.VALIDATION.REQUIRED_FIELD;
            }

            return errors;
        },
        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        resetForm();

        if (selectedService) {
            setFieldValues(selectedService);
        }
    }, [isModalOpened]);

    const onFormSubmit = async (values: ServiceFormValues) => {
        if (selectedService) {
            setIsSubmitting(true);

            const response = await serviceService.updateService({
                id: selectedService.id,
                description: values.description,
                unit: values.unit,
                rowVersion: selectedService.rowVersion,
                status: selectedService.status,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.EDIT_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.EDITED);
        } else {
            setIsSubmitting(true);

            const response = await serviceService.createService({
                description: values.description,
                unit: values.unit,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.SAVE_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.SAVED);
        }

        resetForm();
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                description: '',
                unit: ''
            },
        });
    };

    const setFieldValues = (values: IService) => {
        formik.setFieldValue('description', values.description || '');
        formik.setFieldValue('unit', values.unit || '');
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'lg'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='service-modal'>Manutenção de serviços</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>
                    <CardTabItem id='tab-item-1' title='Dados do serviço' icon='DesignServices'>
                        <div className='row g-4'>
                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <FormGroup id='description' label='Descrição' isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.description}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.description}
                                        invalidFeedback={formik.errors.description}
                                        validFeedback=''
                                    />
                                </FormGroup>
                            </div>
                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <FormGroup id='unit' label='Unidade' isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.unit}
                                        isTouched={formik.touched.unit}
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={formik.handleSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};